/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useState } from 'react';

import { jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, Flex, Text, xcss } from '@atlaskit/primitives';

import { ANALYTIC_TYPE, ANALYTICS_CHANNEL, EVENT_TYPE } from '../../../utils/analytics';
import { SearchFeedbackModal } from '../feedback-modal';
import { type OnSubmitParams } from '../feedback-modal/types';
import { type ArrowOption, getArrowIcon } from '../icons';

import { type SearchFeedbackFormProps } from './types';
interface ButtonOptions {
	icon: ArrowOption;
	label: string;
	value: string;
}

const sideAOptions: ButtonOptions[] = [
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '1', label: 'Left Much Better', icon: 'best-left' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '2', label: 'Left Slightly Better', icon: 'better-left' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '3', label: 'About the Same', icon: 'none' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '4', label: 'Right Slightly Better', icon: 'better-right' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '5', label: 'Right Much Better', icon: 'best-right' },
];

const sideBOptions: ButtonOptions[] = [
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '5', label: 'Left Much Better', icon: 'best-left' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '4', label: 'Left Slightly Better', icon: 'better-left' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '3', label: 'About the Same', icon: 'none' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '2', label: 'Right Slightly Better', icon: 'better-right' },
	// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
	{ value: '1', label: 'Right Much Better', icon: 'best-right' },
];

const feedbackFormStyles = xcss({
	padding: 'space.200',
	display: 'flex',
	flexDirection: 'row',
});

const blogStyles = xcss({
	font: 'font.body.UNSAFE_small',
	fontWeight: 'font.weight.medium',
	textDecoration: 'none',
	color: 'color.text.accent.blue',
	':visited': {
		color: 'color.text.accent.blue',
		textDecoration: 'none',
	},
	':hover': {
		color: 'color.text.accent.blue',
		textDecoration: 'none',
	},
});

const textAndAnchorStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
});

export const SearchFeedbackForm = ({
	isSideAOnLeft,
	onSubmit,
	onRatingClick,
}: SearchFeedbackFormProps): JSX.Element => {
	const [rating, setRating] = useState<string>('');
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnOptionButtonClick = (e: React.MouseEvent, value: string) => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'ratingButton',
			type: ANALYTIC_TYPE.SEND_UI_EVENT,
			eventType: EVENT_TYPE.UI,
			source: ANALYTICS_CHANNEL,
			attributes: {
				value,
				isRedesign: true,
				isSideAOnLeft,
			},
		});
		e.preventDefault();
		setRating(value);
		setIsModalOpen(true);
		onRatingClick();
		analyticsEvent.fire(ANALYTICS_CHANNEL);
	};

	const handleOnSubmitAndClose = ({
		feedback,
		ratingReasons,
		action,
		shouldAttachScreenshot,
	}: OnSubmitParams) => {
		onSubmit({ feedback, atlassianRating: rating, ratingReasons, shouldAttachScreenshot, action });
		setRating('');
		setIsModalOpen(false);
	};

	const isIconAfter = (icon: ArrowOption) => icon === 'best-right' || icon === 'better-right';
	const isIconBefore = (icon: ArrowOption) => icon === 'best-left' || icon === 'better-left';

	const options = isSideAOnLeft ? sideAOptions : sideBOptions;

	return (
		<div data-testid="search-feedback-form">
			<Flex xcss={feedbackFormStyles} alignItems="center" gap="space.200">
				<Box xcss={textAndAnchorStyles}>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Text weight="semibold">Which search results do you prefer?</Text>
					<Anchor
						href="https://hello.atlassian.net/wiki/x/Uo838w"
						xcss={blogStyles}
						target="_blank"
						// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
					>
						Why am I seeing this?
					</Anchor>
				</Box>
				<Flex gap="space.100">
					{options.map((option: ButtonOptions, id: number) => (
						<Button
							key={`${id}-${option}`}
							isSelected={option.value === rating}
							value={option.value}
							onClick={(e) => handleOnOptionButtonClick(e, option.value)}
							{...(isIconBefore(option.icon) &&
								// @ts-ignore - TS2367 TypeScript 5.9.2 upgrade
								option.icon !== 'none' && { iconBefore: getArrowIcon({ option: option.icon }) })}
							{...(isIconAfter(option.icon) &&
								// @ts-ignore - TS2367 TypeScript 5.9.2 upgrade
								option.icon !== 'none' && { iconAfter: getArrowIcon({ option: option.icon }) })}
						>
							{option.label}
						</Button>
					))}
				</Flex>
			</Flex>
			{isModalOpen && <SearchFeedbackModal onSubmitAndClose={handleOnSubmitAndClose} />}
		</div>
	);
};
